// V·IAnet Governance — AdminCatalogos (patron canonico 0479.1c)
// CRUD de catalogos tenant (codigo CAT-NNN, ambito, tipo, nombre, activo)
// Orden canonico: Codigo | Nombre | Descripcion | Ambito | Tipo | Activo | Acciones

import { useState, useEffect, useCallback } from 'react';
import {
  fetchCatalogosTenant,
  crearCatalogoTenant,
  actualizarCatalogoTenant,
  eliminarCatalogoTenant,
  request,
  type CatalogoTenantItem,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { C } from '../../../../../lib/design';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

const TIPO_DESCRIPCIONES: Record<string, string> = {
  GENERO: 'Opciones del campo Genero en la ficha de persona',
  TIPO_CONTRATO: 'Opciones del campo Tipo de Contrato en datos laborales',
  CATEGORIA_LABORAL: 'Opciones del campo Categoria Laboral en datos laborales',
  IDIOMA: 'Idiomas disponibles para el perfil de persona',
};

function EditIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.azul2}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#DC2626"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function PowerIcon({ activo }: { activo: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={activo ? '#16A34A' : '#9CA3AF'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2v10" />
      <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />
    </svg>
  );
}

export function AdminCatalogos() {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const [items, setItems] = useState<CatalogoTenantItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [tipos, setTipos] = useState<string[]>([]);
  const [modal, setModal] = useState<CatalogoTenantItem | 'new' | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (filterTipo) params.tipo = filterTipo;
    if (mostrarInactivos) params.incluirInactivos = 'true';
    fetchCatalogosTenant(params)
      .then((r) => setItems(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filterTipo, mostrarInactivos]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    request<{ data: string[] }>('/catalogos-tenant/tipos')
      .then((r) => setTipos(r.data ?? []))
      .catch(() => {});
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleToggleActivo = async (r: CatalogoTenantItem) => {
    try {
      await actualizarCatalogoTenant(r.id, { activo: !r.activo });
      load();
      showToast(adm.saved);
    } catch {
      showToast('Error');
    }
  };

  const handleBorrar = async (id: string) => {
    try {
      await eliminarCatalogoTenant(id);
      load();
      showToast(adm.saved);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Error');
    } finally {
      setConfirmId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = items
    .filter((it) => (mostrarInactivos ? true : it.activo !== false))
    .filter((it) =>
      q
        ? (it.codigo ?? '').toLowerCase().includes(q) ||
          it.nombre.toLowerCase().includes(q) ||
          it.tipo.toLowerCase().includes(q) ||
          (it.ambito ?? '').toLowerCase().includes(q)
        : true,
    );

  const columns: AdminTableColumn<CatalogoTenantItem>[] = [
    {
      key: 'codigo',
      label: adm.colCodigo ?? 'Codigo',
      sortable: true,
      sortKey: (r) => r.codigo ?? '',
      render: (r) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.azul2 }}>
          {r.codigo && r.codigo.length > 0 ? r.codigo : '—'}
        </span>
      ),
    },
    {
      key: 'nombre',
      label: adm.colNombre ?? 'Nombre',
      sortable: true,
      sortKey: (r) => r.nombre,
      render: (r) => <span style={{ fontWeight: 500 }}>{r.nombre}</span>,
    },
    {
      key: 'descripcion',
      label: adm.colDescripcion ?? 'Descripcion',
      render: (r) => <span style={{ color: '#6B7280', fontSize: 12 }}>{r.descripcion ?? '—'}</span>,
    },
    {
      key: 'ambito',
      label: (adm as unknown as Record<string, string>).ambito ?? 'Ambito',
      sortable: true,
      sortKey: (r) => r.ambito ?? '',
      render: (r) => <span style={{ fontSize: 12 }}>{r.ambito ?? '—'}</span>,
    },
    {
      key: 'tipo',
      label: adm.tipo ?? 'Tipo',
      sortable: true,
      sortKey: (r) => r.tipo,
    },
    {
      key: 'activo',
      label: (adm as unknown as Record<string, string>).activo ?? 'Activo',
      render: (r) => (
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: r.activo ? '#ECFDF5' : '#FEF2F2',
            color: r.activo ? '#16A34A' : '#DC2626',
          }}
        >
          {r.activo ? t.common.estados.activo : t.common.estados.inactivo}
        </span>
      ),
    },
  ];

  const filters = (
    <select
      value={filterTipo}
      onChange={(e) => setFilterTipo(e.target.value)}
      style={{
        padding: '7px 10px',
        border: '1px solid #D1D5DB',
        borderRadius: 6,
        fontSize: 13,
        background: '#fff',
        fontFamily: 'inherit',
        outline: 'none',
      }}
    >
      <option value="">Todos los tipos</option>
      {tipos.map((ty) => (
        <option key={ty} value={ty}>
          {ty}
        </option>
      ))}
    </select>
  );

  return (
    <div>
      <AdminPageHeader
        title={(adm as unknown as Record<string, string>).catalogs ?? adm.catalogos ?? 'Catalogos'}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search,
        }}
        filters={filters}
        onNew={{
          label: adm.newCatalog ?? 'Nuevo catalogo',
          onClick: () => setModal('new'),
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {adm.mostrarInactivos}
          </label>
        }
      />

      {filterTipo && TIPO_DESCRIPCIONES[filterTipo] && (
        <div style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic', marginBottom: 12 }}>
          {TIPO_DESCRIPCIONES[filterTipo]}
        </div>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: C.azul1,
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        onRowClick={(r) => setModal(r)}
        loading={loading}
        loadingMessage={t.common.cargando ?? 'Cargando…'}
        emptyMessage={adm.noResults}
        actionsLabel={adm.acciones}
        actions={(r) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label={adm.tooltips.editar}
              onClick={() => setModal(r)}
            />
            <AdminActionButton
              icon={<PowerIcon activo={r.activo} />}
              label={r.activo ? adm.tooltips.toggleDesactivar : adm.tooltips.toggleActivar}
              onClick={() => handleToggleActivo(r)}
            />
            <AdminActionButton
              icon={<TrashIcon />}
              label={adm.tooltips.eliminar}
              variant="danger"
              onClick={() => setConfirmId(r.id)}
            />
          </>
        )}
      />

      <div
        style={{
          padding: '8px 16px',
          fontSize: 12,
          color: '#6B7280',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        {filtered.length} de {items.length} {adm.catalogosContador ?? 'catalogos'}
      </div>

      {modal !== null && (
        <CatalogoModal
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
            showToast(adm.saved);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title={adm.tooltips.eliminar}
        message={adm.tooltips.eliminar}
        danger
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && handleBorrar(confirmId)}
      />
    </div>
  );
}

function CatalogoModal({
  item,
  onClose,
  onSaved,
}: {
  item: CatalogoTenantItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const isEdit = item !== null;
  const initialForm: Record<string, string> = isEdit
    ? {
        ambito: item.ambito,
        tipo: item.tipo,
        codigo: item.codigo,
        nombre: item.nombre,
        descripcion: item.descripcion ?? '',
        orden: String(item.orden),
      }
    : { orden: '0' };
  const [form, setForm] = useState<Record<string, string>>(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.codigo?.trim() || !form.nombre?.trim() || !form.tipo?.trim()) {
      setError('Tipo, codigo y nombre obligatorios');
      return;
    }
    try {
      setSaving(true);
      const body = { ...form, orden: parseInt(form.orden) || 0 };
      if (isEdit) await actualizarCatalogoTenant(item.id, body);
      else await crearCatalogoTenant(body);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };
  const inp: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    width: '100%',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: '#5A6171',
    marginBottom: 4,
    display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: `3px solid ${C.azul1}`,
          maxWidth: 500,
          width: '95%',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: C.azul1 }}>
          {isEdit ? 'Editar catalogo' : 'Nuevo catalogo'}
        </h2>
        {isEdit && item?.codigo && (
          <div
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              color: C.azul2,
              marginBottom: 16,
            }}
          >
            {item.codigo}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div>
            <label style={lbl}>Ambito</label>
            <input
              style={inp}
              value={form.ambito ?? ''}
              onChange={(e) => set('ambito', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Tipo *</label>
            <input
              style={inp}
              value={form.tipo ?? ''}
              onChange={(e) => set('tipo', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Codigo *</label>
            <input
              style={inp}
              value={form.codigo ?? ''}
              onChange={(e) => set('codigo', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Nombre *</label>
            <input
              style={inp}
              value={form.nombre ?? ''}
              onChange={(e) => set('nombre', e.target.value)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Descripcion</label>
            <input
              style={inp}
              value={form.descripcion ?? ''}
              onChange={(e) => set('descripcion', e.target.value)}
            />
          </div>
          <div>
            <label style={lbl}>Orden</label>
            <input
              type="number"
              style={inp}
              value={form.orden ?? '0'}
              onChange={(e) => set('orden', e.target.value)}
            />
          </div>
        </div>
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginTop: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            {t.common.cancelar}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: C.azul2,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? t.common.guardando : t.common.guardar}
          </button>
        </div>
      </div>
    </div>
  );
}
